/**
 * C3-Aktivitäts-/Verlauf-Sidebar neben dem Channels-Editor.
 *
 * Feed aus Content-Block-Versionen (useProductActivity), Filter „Alle Kanäle /
 * Nur Connector", Zeitgruppen (Diese Woche / Dieser Monat / je Monat).
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { History, ArrowRight } from 'lucide-react'
import { Loading } from '@/components/v2/ui'
import { cn } from '@/lib/utils'
import { EID } from '@/lib/element-ids.registry'
import { groupEntries, type ActivityEntry } from './activityFeed'
import { ActivityEntryRow } from './ActivityEntryRow'

/** Max. Einträge im Sidebar-Feed je Filter — Vollverlauf via Historie-Tab. */
const MAX_ENTRIES = 15

interface Props {
  entries: ActivityEntry[]
  isLoading: boolean
  onShow: (entry: ActivityEntry) => void
}

function groupLabel(key: string, t: (k: string) => string, lang: string): string {
  if (key === 'thisWeek') return t('productContent.channels.activity.thisWeek')
  if (key === 'thisMonth') return t('productContent.channels.activity.thisMonth')
  const [y, m] = key.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString(lang, { month: 'long', year: 'numeric' })
}

export function ContentActivitySidebar({ entries, isLoading, onShow }: Props) {
  const { t, i18n } = useTranslation()
  const [, setSearchParams] = useSearchParams()
  const [connectorOnly, setConnectorOnly] = useState(false)
  const filtered = connectorOnly ? entries.filter((e) => e.isConnector) : entries
  const limited = filtered.slice(0, MAX_ENTRIES)
  const groups = useMemo(() => groupEntries(limited), [limited])

  return (
    <aside
      className="content-activity-sidebar w-80 shrink-0 border-l border-neutral-200 dark:border-neutral-800 flex flex-col min-h-0"
      aria-label={t('productContent.channels.activity.title')}
    >
      <header className="px-3 py-2 border-b border-neutral-200 dark:border-neutral-800">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-neutral-900 dark:text-neutral-50">
          <History className="h-4 w-4" aria-hidden="true" />
          {t('productContent.channels.activity.title')}
        </h3>
        <div role="group" aria-label={t('productContent.channels.activity.filterLabel')} className="flex gap-1 mt-2">
          {[
            { v: false, label: t('productContent.channels.activity.allChannels'), eid: EID['content-activity-filter-all-btn'] },
            { v: true, label: t('productContent.channels.activity.connectorOnly'), eid: EID['content-activity-filter-connector-btn'] },
          ].map((opt) => (
            <button
              key={String(opt.v)}
              type="button"
              id={opt.eid}
              aria-pressed={connectorOnly === opt.v}
              onClick={() => setConnectorOnly(opt.v)}
              className={cn(
                'text-xs px-2 py-1 rounded-full border',
                connectorOnly === opt.v
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-auto p-3 space-y-4">
        {isLoading && <div className="flex justify-center py-6"><Loading size="sm" /></div>}
        {!isLoading && filtered.length === 0 && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-6">
            {t('productContent.channels.activity.empty')}
          </p>
        )}
        {groups.map((group) => (
          <section key={group.key} aria-label={groupLabel(group.key, t, i18n.language)}>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-400 mb-1.5">
              {groupLabel(group.key, t, i18n.language)}
            </h4>
            <ul className="space-y-2">
              {group.entries.map((entry) => (
                <ActivityEntryRow key={entry.id} entry={entry} onShow={onShow} />
              ))}
            </ul>
          </section>
        ))}
      </div>

      <footer className="border-t border-neutral-200 dark:border-neutral-800 p-3">
        {filtered.length > MAX_ENTRIES && (
          <p className="text-xs text-neutral-400 mb-2 text-center">
            {t('productContent.channels.activity.showingLast', { count: MAX_ENTRIES, total: filtered.length })}
          </p>
        )}
        <button
          type="button"
          id={EID['content-activity-history-btn']}
          className="content-activity-history-btn flex w-full items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-neutral-200 dark:border-neutral-700 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
          onClick={() => setSearchParams({ tab: 'history' })}
        >
          {t('productContent.channels.activity.toHistory')}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </footer>
    </aside>
  )
}
